import { AlertTriangle, CalendarRange, Loader2 } from "lucide-react";

import { Skeleton } from "@/components/ui/skeleton";
import { PendingExpired, PendingWindow } from "@/services/price-remark/types";
import { formatDateToDMY } from "@/utils/format-date-ddmmyyyy";
import { formatNumber } from "@/utils/formatToMoney";

interface WindowBarProps {
  window?: PendingWindow;
  durationMs?: number;
  cache?: boolean;
  expired?: PendingExpired;
  isLoadingExpired?: boolean;
  storeScope?: boolean;
}

function Tag({ children }: { children: React.ReactNode }) {
  return (
    <span className="rounded bg-gray-100 px-2 py-0.5 font-mono text-[11px] text-gray-700">
      {children}
    </span>
  );
}

// O passivo é subestimado por construção: item que saiu da janela e cujo preço
// virou corrente depois não é mais contado. Mede o que ainda é dívida hoje.
function ExpiredLine({
  expired,
  storeScope,
}: {
  expired: PendingExpired;
  storeScope: boolean;
}) {
  if (!expired.applicable) {
    return (
      <p className="mt-1.5 w-full text-[11px] text-gray-400">
        Sem faixa a apurar: a janela do coletor cobre os últimos {expired.days}{" "}
        dias inteiros, então nenhum lote saiu dela no período.
      </p>
    );
  }

  if (expired.items === 0) {
    return (
      <p className="mt-1.5 w-full text-[11px] text-green-700">
        Nenhum lote saiu da janela sem remarcação nos últimos {expired.days}{" "}
        dias.
      </p>
    );
  }

  return (
    <p className="mt-1.5 flex w-full items-start gap-1.5 text-[11px] font-semibold text-red-600">
      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      <span>
        {storeScope
          ? `${formatNumber(expired.items)} itens saíram da janela sem remarcação nos últimos ${expired.days} dias`
          : `${formatNumber(expired.stores)} loja(s) perderam lote na janela sem remarcação nos últimos ${expired.days} dias — ${formatNumber(expired.items)} itens`}
        <span className="ml-1 font-normal text-gray-400">
          esses itens não voltam para o coletor
        </span>
      </span>
    </p>
  );
}

// A linha fica reservada desde o primeiro render porque a apuração vem numa
// requisição própria — senão o alerta surge do nada com a tabela já pronta.
function ExpiredLoading() {
  return (
    <p className="mt-1.5 flex w-full items-center gap-1.5 text-[11px] text-gray-400">
      <Loader2 className="h-3.5 w-3.5 animate-spin" />
      Apurando os lotes que saíram da janela sem remarcação…
    </p>
  );
}

export function WindowBar({
  window: collectorWindow,
  durationMs,
  cache,
  expired,
  isLoadingExpired = false,
  storeScope = false,
}: WindowBarProps) {
  if (!collectorWindow) {
    return <Skeleton className="h-10 w-full" />;
  }

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-md border border-gray-200 bg-white px-3 py-2 text-xs text-gray-700">
      <span className="flex items-center gap-1.5 font-semibold">
        <CalendarRange className="h-4 w-4 text-gray-400" />
        Janela do coletor
      </span>
      <Tag>
        {formatDateToDMY(collectorWindow.from)} a{" "}
        {formatDateToDMY(collectorWindow.to)}
      </Tag>
      <Tag>go-live {formatDateToDMY(collectorWindow.launchDate)}</Tag>
      <Tag>{collectorWindow.retroactiveDays} dias retroativos</Tag>
      <span className="text-gray-400">
        Fora dela o operador não vê o lote no aparelho.
      </span>
      {durationMs != null && (
        <span className="ml-auto rounded bg-green-50 px-2 py-0.5 font-mono text-[11px] font-semibold text-green-700">
          {cache ? "cache · " : ""}
          {(durationMs / 1000).toLocaleString("pt-BR", {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
          })}{" "}
          s
        </span>
      )}
      {isLoadingExpired ? (
        <ExpiredLoading />
      ) : expired ? (
        <ExpiredLine expired={expired} storeScope={storeScope} />
      ) : null}
    </div>
  );
}

export default WindowBar;
